<script setup>
import { ref } from "vue";
import { useRouter } from "vue-router";
import { saveUser, autoCreateTasks } from "./api";

const router = useRouter();
const csvFileName = ref("");
const users = ref([]);
const saving = ref(false);
const error = ref("");
const success = ref("");

// Backend field -> accepted CSV header variants (normalized: lowercased, non-alphanumeric
// stripped). Kept as a single source of truth so header-presence checks and per-row
// lookups stay in sync.
const FIELD_ALIASES = {
  customer_id: ["customerid", "employeenumber", "employeeno", "employeeid"],
  name: ["name", "formalname", "officialname"],
  display_name: ["displayname", "preferredname"],
  company: ["company", "companyname"],
  department: ["department", "departmentname"],
  job_title: ["jobtitle"],
  level: ["level"],
  dob: ["dob", "dateofbirth"],
  hiring_date: ["hiringdate", "hiredate"],
  job_classification: ["jobclassification"],
  proxy_number: ["proxynumber"],
};
const REQUIRED_FIELDS = [
  ["customer_id", "Employee Number"],
  ["name", "Formal Name"],
  ["company", "Company Name"],
  ["department", "Department Name"],
  ["job_title", "Job Title"],
];

// Parses a CSV into { headers, rows } — headers are normalized (lowercased,
// non-alphanumeric stripped) so exports like "Employee Number", "Formal Name",
// "Job Classification" all map to a consistent key regardless of spacing/casing.
function parseCsv(text) {
  const lines = text.trim().split(/\r?\n/);
  if (lines.length < 2) return { headers: [], rows: [] };
  const headers = lines[0].split(",").map((h) => h.replace(/^\uFEFF/, "").trim().toLowerCase().replace(/[^a-z0-9]/g, ""));
  const rows = lines.slice(1).filter((line) => line.trim()).map((line) => {
    const values = line.split(",");
    return Object.fromEntries(headers.map((h, i) => [h, (values[i] ?? "").trim()]));
  });
  return { headers, rows };
}

// Returns the labels of any required columns that have no matching header at all
// (as opposed to a header that's present but blank for a given row).
function missingRequiredColumns(headers) {
  return REQUIRED_FIELDS
    .filter(([field]) => !FIELD_ALIASES[field].some((alias) => headers.includes(alias)))
    .map(([, label]) => label);
}

// The backend's /users/update_add_user expects a fixed payload shape (customer_id,
// name, display_name, company, department, job_title, dob, hiring_date, level,
// job_classification, proxy_number) — CSV exports use human-readable headers
// (e.g. "Employee Number", "Formal Name"), so map each row's normalized keys to
// the field(s) the API actually understands. A header/alias with no column in the
// CSV simply yields "" here — required-ness is enforced separately in submit().
function toUser(row) {
  const get = (keys) => { for (const k of keys) { if (row[k] !== undefined && row[k] !== "") return row[k]; } return ""; };
  const jobClassification = get(FIELD_ALIASES.job_classification);
  const [jobCode, jobDescription] = jobClassification.split("-").map((s) => s.trim());
  return {
    customer_id: get(FIELD_ALIASES.customer_id),
    name: get(FIELD_ALIASES.name),
    display_name: get(FIELD_ALIASES.display_name),
    company: get(FIELD_ALIASES.company),
    department: get(FIELD_ALIASES.department),
    job_title: get(FIELD_ALIASES.job_title),
    level: get(FIELD_ALIASES.level),
    dob: get(FIELD_ALIASES.dob),
    hiring_date: get(FIELD_ALIASES.hiring_date),
    job_classification: { job_code: jobCode || "", job_description: jobDescription || "" },
    proxy_number: get(FIELD_ALIASES.proxy_number),
  };
}

function onFile(e) {
  const file = e.target.files?.[0]; e.target.value = "";
  if (!file) return;
  error.value = ""; success.value = ""; users.value = []; csvFileName.value = "";
  const reader = new FileReader();
  reader.onload = (ev) => {
    const { headers, rows } = parseCsv(ev.target.result);
    if (!rows.length) { error.value = "No data rows found in CSV."; return; }
    const missing = missingRequiredColumns(headers);
    if (missing.length) {
      error.value = `CSV is missing required column(s): ${missing.join(", ")}.`;
      return;
    }
    users.value = rows.map(toUser);
    csvFileName.value = file.name;
  };
  reader.onerror = () => { error.value = "Unable to read the selected file."; };
  reader.readAsText(file);
}

async function submit() {
  error.value = ""; success.value = "";
  const valid = users.value.filter((u) => u.customer_id && u.name && u.company && u.department && u.job_title);
  const skipped = users.value.length - valid.length;
  if (!valid.length) { error.value = "No valid rows found in CSV. Each row needs Employee Number, Name, Company, Department and Job Title filled in."; return; }
  saving.value = true;
  try {
    const guids = [];
    for (const u of valid) {
      const result = await saveUser(u);
      if (result?.guid) guids.push(result.guid);
    }
    if (guids.length) { await autoCreateTasks(guids).catch(() => {}); }
    success.value = skipped
      ? `${valid.length} user(s) processed successfully. ${skipped} row(s) skipped due to missing required fields.`
      : `${valid.length} user(s) processed successfully.`;
    users.value = [];
    csvFileName.value = "";
  } catch { error.value = "Error processing batch. Please try again."; }
  finally { saving.value = false; }
}
</script>

<template>
  <section>
    <VBtn variant="text" prepend-icon="mdi:arrow-left" class="mb-4" @click="router.push({ name: 'users' })">Back</VBtn>
    <h1 class="text-h5 mb-4">Batch Upload Users</h1>
    <VCard class="pa-4" max-width="800">
      <VAlert v-if="error" type="error" class="mb-4">{{ error }}</VAlert>
      <VAlert v-if="success" type="success" class="mb-4">{{ success }}</VAlert>
      <div class="mb-4">
        <div class="text-body-2 font-weight-semibold mb-1">CSV File (header row: <code>Employee Number, Formal Name, Preferred Name, Company Name, Department Name, Job Title, Date of Birth, Hiring Date, Level, Job Classification, Proxy Number</code>)</div>
        <input type="file" accept=".csv" @change="onFile" />
        <p v-if="csvFileName" class="text-caption text-medium-emphasis mt-1">{{ csvFileName }} — {{ users.length }} row(s)</p>
      </div>
      <VBtn :loading="saving" :disabled="!users.length" @click="submit">Upload Users</VBtn>
    </VCard>
  </section>
</template>
